import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Search, Globe, MapPin, ShieldAlert } from "lucide-react";

const VERDICT_COLORS = {
  clean: "#34C759",
  suspicious: "#FF9F0A",
  malicious: "#FF3B30",
  unknown: "#8E9196",
};

export default function ThreatIntel() {
  const [indicator, setIndicator] = useState("");
  const [type, setType] = useState("auto");
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadHistory = async () => {
    const { data } = await api.get("/threat-intel/history");
    setHistory(data);
  };

  useEffect(() => { loadHistory(); }, []);

  const lookup = async (e) => {
    e.preventDefault();
    if (!indicator.trim()) return;
    setLoading(true);
    try {
      const payload = { indicator: indicator.trim() };
      if (type !== "auto") payload.indicator_type = type;
      const { data } = await api.post("/threat-intel/lookup", payload);
      setResult(data);
      loadHistory();
    } catch (err) {
      toast.error("Lookup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 max-w-[1400px] mx-auto">
      <header className="mb-6">
        <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196]">IOC validation · VirusTotal + Hybrid Analysis</div>
        <h1 className="font-display font-black text-5xl mt-2">Threat Intelligence</h1>
        <p className="text-[#8E9196] mt-1">Check IP, domain, URL, or file hash across integrated feeds.</p>
      </header>

      <form onSubmit={lookup} className="flex gap-2 mb-6" data-testid="threat-form">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8E9196]" />
          <input
            data-testid="threat-indicator"
            value={indicator}
            onChange={(e) => setIndicator(e.target.value)}
            placeholder="e.g. 185.220.101.45  |  malware.example.com  |  44d88612fea8a8f36de82e1278abb02f"
            className="w-full bg-transparent border border-white/15 pl-10 pr-3 py-3 rounded-sm font-mono text-sm outline-none focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF]"
          />
        </div>
        <select value={type} onChange={(e) => setType(e.target.value)} data-testid="threat-type"
          className="bg-[#0a0a0a] border border-white/15 px-3 py-3 rounded-sm font-mono text-sm outline-none focus:border-[#00F0FF]">
          {["auto", "ip", "domain", "url", "hash"].map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <button data-testid="threat-submit" disabled={loading}
          className="border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black px-5 py-3 rounded-sm font-mono uppercase text-xs tracking-widest disabled:opacity-50">
          {loading ? "Scanning…" : "Lookup"}
        </button>
      </form>

      {result && (
        <div data-testid="threat-result">
          <div className="border border-white/10 bg-[#121212] rounded-sm p-5 mb-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196]">Indicator</div>
                <div className="font-mono text-lg text-white">{result.indicator}</div>
                <div className="font-mono text-xs text-[#8E9196] mt-1 uppercase tracking-widest">Type: {result.indicator_type}</div>
              </div>
              <div className="text-right">
                <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196]">Aggregate verdict</div>
                <div className="font-display font-black text-3xl uppercase" style={{ color: VERDICT_COLORS[result.aggregate_verdict] }}>
                  {result.aggregate_verdict}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {result.verdicts.map((v) => (
              <div key={v.source} className="border border-white/10 bg-[#121212] rounded-sm p-5" data-testid={`verdict-${v.source.split(" ")[0].toLowerCase()}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="font-display text-xl">{v.source}</div>
                  <span className="font-mono text-xs uppercase tracking-widest px-2 py-0.5 rounded-sm border"
                    style={{ color: VERDICT_COLORS[v.verdict], borderColor: VERDICT_COLORS[v.verdict] + "55" }}>
                    {v.verdict}
                  </span>
                </div>

                <div className="grid grid-cols-4 gap-2 font-mono text-center">
                  {[
                    { k: "malicious", c: "#FF3B30" },
                    { k: "suspicious", c: "#FF9F0A" },
                    { k: "harmless", c: "#34C759" },
                    { k: "undetected", c: "#8E9196" },
                  ].map(({ k, c }) => (
                    <div key={k} className="border border-white/10 rounded-sm p-2">
                      <div className="text-2xl font-display font-black" style={{ color: c }}>{v[k]}</div>
                      <div className="text-[10px] uppercase tracking-widest text-[#8E9196] mt-1">{k}</div>
                    </div>
                  ))}
                </div>

                {v.detections?.length > 0 && (
                  <div className="mt-4">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mb-1">Detections</div>
                    <div className="flex flex-wrap gap-1">
                      {v.detections.map((d) => <span key={d} className="font-mono text-[11px] border border-[#FF3B30]/40 text-[#FF3B30] rounded-sm px-1.5 py-0.5">{d}</span>)}
                    </div>
                  </div>
                )}

                {(v.country || v.asn) && (
                  <div className="mt-3 flex items-center gap-3 text-[11px] font-mono text-[#8E9196] uppercase tracking-widest">
                    {v.country && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {v.country}</span>}
                    {v.asn && <span className="truncate">{v.asn}</span>}
                  </div>
                )}

                {v.raw?.note && (
                  <div className="mt-3 text-[10px] font-mono text-[#52525B]">{v.raw.note}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="border border-white/10 bg-[#121212] rounded-sm" data-testid="threat-history">
        <div className="px-4 py-3 border-b border-white/10 text-[11px] font-mono uppercase tracking-widest text-[#8E9196] flex items-center gap-2">
          <ShieldAlert className="w-3.5 h-3.5" /> Lookup history
        </div>
        <div className="divide-y divide-white/5 max-h-80 overflow-y-auto">
          {history.map((h) => (
            <button key={h.id}
              onClick={() => { setIndicator(h.indicator); setType(h.indicator_type); }}
              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/[0.02] text-left">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: VERDICT_COLORS[h.aggregate_verdict] }} />
              <span className="font-mono text-xs text-white truncate flex-1">{h.indicator}</span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-[#8E9196]">{h.indicator_type}</span>
              <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: VERDICT_COLORS[h.aggregate_verdict] }}>{h.aggregate_verdict}</span>
              <span className="font-mono text-[10px] text-[#52525B]">{new Date(h.timestamp).toLocaleString()}</span>
            </button>
          ))}
          {history.length === 0 && <div className="text-sm text-[#52525B] p-4">No lookups yet.</div>}
        </div>
      </div>
    </div>
  );
}
